import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

interface BannerSlide {
  _id: string;
  image: string;
  order: number;
  isActive: boolean;
}

interface FullScreenBannerSliderProps {
  banners: BannerSlide[];
  autoPlayInterval?: number;
}

const FullScreenBannerSlider: React.FC<FullScreenBannerSliderProps> = ({ 
  banners, 
  autoPlayInterval = 5000 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Safe banner processing with error handling
  let activeBanners: BannerSlide[] = [];
  try {
    // Filter only active banners and sort by order
    activeBanners = (banners || [])
      .filter(banner => banner && banner.isActive !== false)
      .sort((a, b) => (a?.order || 0) - (b?.order || 0));
  } catch (err) {
    console.error('FullScreenBannerSlider: Error processing banners:', err);
    setHasError(true);
  }

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % activeBanners.length);
  }, [activeBanners.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + activeBanners.length) % activeBanners.length);
  }, [activeBanners.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Resume autoplay after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || activeBanners.length <= 1) return;

    const interval = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, goToNext, activeBanners.length]);

  if (hasError || activeBanners.length === 0) {
    return null;
  }
  
  if (activeBanners.length === 1) {
    try {
      const bannerImage = getImageUrl(activeBanners[0]?.image);
      
      return (
        <section className="relative w-full aspect-[4/3] sm:aspect-[16/9] md:aspect-[21/9] lg:h-[80vh] overflow-hidden bg-gray-100">
          <img
            src={bannerImage}
            alt="Banner"
            className="w-full h-full object-contain sm:object-cover object-center"
            onError={(e) => {
              console.error('Banner image failed to load:', bannerImage);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </section>
      );
    } catch (err) {
      console.error('Error rendering single banner:', err);
      return null;
    }
  }

  return (
    <section className="relative w-full aspect-[4/3] sm:aspect-[16/9] md:aspect-[21/9] lg:h-[80vh] overflow-hidden bg-gray-100 group">
      {/* Slides */}
      {activeBanners.map((banner, index) => (
        <div
          key={banner._id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={getImageUrl(banner?.image)}
            alt={`Banner ${index + 1}`}
            className="w-full h-full object-contain sm:object-cover object-center"
            onError={(e) => {
              console.error(`Banner ${index + 1} image failed to load:`, banner?.image);
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      ))}

      {/* Navigation Arrows - Show on hover */}
      <button
        onClick={goToPrev}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {activeBanners.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-white w-8' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      {isAutoPlaying && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
          <div 
            className="h-full bg-white/60 transition-all duration-100 ease-linear progress-bar"
          />
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default FullScreenBannerSlider;
